import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { SITE, waLink, API_URL, INTERESTS, BILL_RANGES, UFS } from "../data/site";
import { Overline, SectionTitle, Accent, Reveal } from "./Reveal";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#04120E]/70 px-4 py-3.5 text-sm text-white placeholder:text-emerald-100/35 outline-none focus:border-[#00E676]/60 focus:ring-2 focus:ring-[#00E676]/25 transition-[border-color,box-shadow] duration-300";

export const LeadForm = ({ overline = "Vamos começar?", title = null, subtitle = null, defaultInterest = null }) => {
  const [form, setForm] = useState({ name: "", whatsapp: "", interest: defaultInterest || INTERESTS[0], bill_range: "", uf: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Informe o seu nome.");
      return;
    }
    setSending(true);
    const message = `Oi, eu sou ${form.name}. Quero: ${form.interest}. Conta de luz: ${form.bill_range || "não informada"}. Estado: ${form.uf || "não informado"}.`;
    try {
      await axios.post(`${API_URL}/leads`, {
        name: form.name.trim(),
        whatsapp: form.whatsapp || null,
        interest: form.interest,
        bill_range: form.bill_range || null,
        uf: form.uf || null,
        source: "formulario-final",
      });
      setDone(true);
      toast.success("Recebemos seus dados! Abrindo o WhatsApp…");
      window.open(waLink(message), "_blank", "noopener");
    } catch (err) {
      toast.error("Não conseguimos salvar agora, mas você pode falar direto no WhatsApp.");
      window.open(waLink(message), "_blank", "noopener");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="lead-form" className="relative py-24 sm:py-32 bg-[#04120E] overflow-hidden" data-testid="lead-form-section">
      <div className="absolute top-10 -left-32 w-[460px] h-[460px] rounded-full bg-[#00E676]/8 blur-[130px]" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>{overline}</Overline>
          <SectionTitle>
            {title || (<>Sua próxima conta de luz pode <Accent>custar menos</Accent>.</>)}
          </SectionTitle>
          <p className="mt-5 max-w-md text-sm sm:text-base text-emerald-100/60 leading-relaxed">
            {subtitle || "Me conta onde você mora e quanto paga de luz. Eu confiro o desconto da sua região e te explico tudo pelo WhatsApp."}
          </p>
          <p className="mt-8 text-xs text-emerald-100/45 leading-relaxed max-w-md border-t border-white/10 pt-6">
            Atendimento por {SITE.licenciado} · Licenciado Autorizado iGreen Energy (ID {SITE.licenciadoId}).{" "}
            Prefere fazer sozinho?{" "}
            <a href={SITE.cadastroUrl} target="_blank" rel="noopener noreferrer" className="text-[#00E676] underline underline-offset-4" data-testid="self-signup-link">
              Cadastre sua conta de luz aqui
            </a>
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-3xl border border-[#00E676]/20 bg-[#0B261E]/70 backdrop-blur-md p-8 sm:p-10" data-testid="lead-form-card">
            {done ? (
              <div className="text-center py-10" data-testid="lead-form-success">
                <CheckCircle2 size={52} className="mx-auto text-[#00E676]" />
                <h3 className="mt-5 font-heading text-2xl font-bold text-white">Pronto!</h3>
                <p className="mt-3 text-sm text-emerald-100/60 max-w-xs mx-auto leading-relaxed">
                  Abrimos o WhatsApp numa nova aba. Se não abriu,{" "}
                  <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-[#00E676] underline underline-offset-4">
                    toque aqui
                  </a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">Quanto você pode economizar?</h3>
                  <p className="mt-1.5 text-xs text-emerald-100/50">Responda 3 perguntas e fale comigo no WhatsApp. Sem compromisso.</p>
                </div>
                <div>
                  <label htmlFor="lead-name" className="block text-xs font-semibold text-emerald-100/70 mb-2">Seu nome</label>
                  <input
                    id="lead-name"
                    data-testid="lead-form-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Como posso te chamar?"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lead-whatsapp" className="block text-xs font-semibold text-emerald-100/70 mb-2">Seu WhatsApp com DDD (opcional)</label>
                  <input
                    id="lead-whatsapp"
                    data-testid="lead-form-whatsapp-input"
                    value={form.whatsapp}
                    onChange={set("whatsapp")}
                    placeholder="(17) 99999-0000"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="lead-interest" className="block text-xs font-semibold text-emerald-100/70 mb-2">O que você procura?</label>
                  <select id="lead-interest" data-testid="lead-form-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                    {INTERESTS.map((i) => <option key={i} value={i} className="bg-[#04120E]">{i}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="lead-bill" className="block text-xs font-semibold text-emerald-100/70 mb-2">Sua conta de luz por mês</label>
                    <select id="lead-bill" data-testid="lead-form-bill-select" value={form.bill_range} onChange={set("bill_range")} className={inputCls}>
                      <option value="" className="bg-[#04120E]">Escolha</option>
                      {BILL_RANGES.map((b) => <option key={b} value={b} className="bg-[#04120E]">{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="lead-uf" className="block text-xs font-semibold text-emerald-100/70 mb-2">Estado</label>
                    <select id="lead-uf" data-testid="lead-form-uf-select" value={form.uf} onChange={set("uf")} className={inputCls}>
                      <option value="" className="bg-[#04120E]">UF</option>
                      {UFS.map((u) => <option key={u} value={u} className="bg-[#04120E]">{u}</option>)}
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="lead-form-submit-button"
                  className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] disabled:opacity-60 text-[#04120E] font-bold px-7 py-4 shadow-[0_0_24px_rgba(0,230,118,0.4)] transition-[background-color,transform,opacity] duration-300 hover:scale-[1.02]"
                >
                  <Send size={17} /> {sending ? "Enviando…" : "Falar no WhatsApp"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
